// Angular Files
import { Inject, Injectable, Injector } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Router } from '@angular/router';

// Angular Material Files
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';

// Other External Files
import { firstValueFrom, take } from 'rxjs';

// Payment Integration Files
import {
    IFramePaymentResponse,
    PaymentMethodData,
    PaymentMethodTypeEnum,
    PaymentMethodTypeEnumConvertor,
    RedirectPaymentProcessorResponse,
    IFrameSavePaymentMethodResponse,
} from 'apps/public-portal/src/app/payment-integrations/base/models';
import { PaymentProcessorService } from 'apps/public-portal/src/app/payment-integrations/base/interfaces/PaymentProcessorService';
import { PaymentProcessorProvider } from 'apps/public-portal/src/app/payment-integrations/base/service/payment-processor.provider';
import { CartPartitionRef, CartPartitionSessionToken, IFramePaymentPrepareResponse } from 'apps/public-portal/src/app/payment-integrations/base/models/IFramePaymentPrepareResponse';
import {
    CvvPromptDialogComponent,
    CvvPromptDialogData,
} from 'apps/public-portal/src/app/payment-integrations/generic/component/cvv-prompt-dialog/cvv-prompt-dialog.component';

// Teller Online Files
import {
    CartPartitionRefDto,
    CartPartitionSessionTokenDto,
    ConvenienceFeeDto,
    ConvenienceFeeResponseDto,
    IFramePaymentRequestDto,
    IFrameSavePaymentMethodRequestDto,
    IFrameSavePaymentMethodResponseDto,
    SharedPaymentIntegrationApiClient,
    GenericIntegrationApiClient,
    RedirectVerifyPaymentStatusRequestDto,
    ReTokenizeSavedPaymentMethodRequestDto,
    StartPaymentResponseDto,
} from '@teller-online/public-portal/core/api/PaymentIntegrationApiClients';
import {
    AuthService,
    CartService,
    InboundRedirectService,
} from '@teller-online/public-portal/core/services';
import { TellerOnlineMessageService, TellerOnlineWindowService } from 'teller-online-libraries/shared';
import { TellerOnlineAppService } from 'teller-online-libraries/core';

@Injectable({
    providedIn: 'root',
})
export class GenericService extends PaymentProcessorService {
    private static readonly PAYMENT_IDENTIFIER_KEY = 'genericPaymentIdentifier';

    // Per-partition session tokens cached after the most recent prepare so the
    // next payCart can round-trip them to the backend. Empty when the active
    // plugin doesn't initialize a per-payment session.
    private _partitionSessionTokens: CartPartitionSessionToken[] = [];

    // Loaded via assignAdditionalProps from backend additionalData
    public vgsEnvironment: string;

    public vgsVaultId: string;

    public vgsVersion: string;

    /**
     * Whether the active plugin uses a hosted-page redirect (no iframe).
     * Sourced from <c>PaymentProcessorProvider.pluginConfig.isRedirect</c> —
     * which the StartPayment endpoint populates from the per-(system, plugin)
     * <c>OnlinePaymentConfig.IsRedirect</c> the plugin advertises.
     *
     * Resolved lazily via the Injector to avoid circular DI: PaymentProcessor
     * Provider's constructor calls <c>injector.get(GenericService)</c> while
     * still constructing itself, so an eager constructor injection would
     * fail with a "Circular dependency" error.
     */
    public override get redirect(): boolean {
        return this._injector
            .get(PaymentProcessorProvider)
            .pluginConfig?.isRedirect ?? false;
    }

    public override get hasPrepareStep() { return true; }

    public override async preparePayment(params): Promise<IFramePaymentPrepareResponse> {
        return this.preparePaymentAndAssessFees(params);
    }

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private router: Router,
        private appService: TellerOnlineAppService,
        private genericClient: GenericIntegrationApiClient,
        private authService: AuthService,
        private cartService: CartService,
        private inboundRedirectService: InboundRedirectService,
        private messageService: TellerOnlineMessageService,
        private windowService: TellerOnlineWindowService,
        private dialog: MatDialog,
        public sharedPaymentApi: SharedPaymentIntegrationApiClient,
        private _injector: Injector,
    ) {
        super(sharedPaymentApi);

        // Clear the stored payment identifier when the user signs out so it
        // doesn't leak across sessions on a shared device. Service is
        // providedIn: 'root' so this subscription lives for the app lifetime.
        this.authService.authDetailsChange.subscribe(details => {
            if (!details) {
                this.windowService.removeLocalStorageItem(GenericService.PAYMENT_IDENTIFIER_KEY);
            }
        });
    }

    public override async checkout(cartGuid: string) {
        if (!this.redirect) {
            this.router.navigate(['/checkout']);
            return;
        }

        let startPaymentResponse: StartPaymentResponseDto;

        try {
            startPaymentResponse = await firstValueFrom(
                this.sharedPaymentApi.startPayment(
                    cartGuid,
                    undefined,
                    this.inboundRedirectService.redirectSourceId
                )
            );
        } catch (e) {
            // Surface the backend's actual message (from TellerOnlineException
            // or the global exception handler's error envelope) so the user sees
            // the real reason instead of a hardcoded generic. The cart was
            // unlocked server-side in StartPaymentAsync's catch, so the user
            // can retry after fixing the underlying cause.
            const message = e?.errorMessage
                ?? e?.message
                ?? 'Failed to initialize payment and generate redirect url';

            this.messageService.notification(message, 'error', 6000);

            // Still emit on _errorEvent so the spinner-cleanup subscriber in
            // app.component fires.
            this._errorEvent.next(message);
            return;
        }

        const { redirectUrl } = startPaymentResponse;

        if (!redirectUrl) {
            const message = 'Failed to redirect';
            this.messageService.notification(message, 'error', 6000);
            this._errorEvent.next(message);
            return;
        }

        this.windowService.setLocalStorageItem(
            GenericService.PAYMENT_IDENTIFIER_KEY,
            startPaymentResponse.paymentIdentifier
        );

        const formInputs = startPaymentResponse.redirectFormInputs ?? [];
        const usePost = startPaymentResponse.redirectMethod?.toUpperCase() === 'POST'
            || formInputs.length > 0;

        try {
            if (!usePost) {
                this.windowService.navigateExternalUrl(redirectUrl);
                return;
            }

            const form = this.document.createElement('form');
            form.method = 'POST';
            form.action = redirectUrl;

            // Snapshot cart total once for {cartTotal} substitution. We don't
            // resolve billing fields ({phone}, address, ...) here because the
            // redirect flow hands the user to the PSP's hosted page before
            // they enter any of that.
            const cartTotal = (await firstValueFrom(
                this.cartService.cartTotals$.pipe(take(1))
            ))?.aggregateTotal;

            for (const input of formInputs) {
                const resolvedValue = this.resolvePresetToken(
                    input.value,
                    startPaymentResponse,
                    cartGuid,
                    cartTotal
                );

                this.addFormInput(form, input.name, resolvedValue);
            }

            this.document.body.append(form);
            form.submit();
        } catch (e) {
            const message = 'Failed to redirect';
            this.messageService.notification(message, 'error', 6000);
            this._errorEvent.next(message);
        }
    }

    public override async handlePostbackResponse(params) {
        const verifyRequest = new RedirectVerifyPaymentStatusRequestDto();

        const paymentIdentifier = this.windowService.getLocalStorageItem(GenericService.PAYMENT_IDENTIFIER_KEY);
        verifyRequest.paymentIdentifier = paymentIdentifier;
        verifyRequest.paymentStatus = params.result;
        verifyRequest.paymentIntegrationRef = params.ref ?? paymentIdentifier;

        let response;

        if (paymentIdentifier) {
            response = await firstValueFrom(
                this.sharedPaymentApi
                    .verifyPaymentStatus(verifyRequest)
            );

            // We've consumed the identifier — clear it so a later stale
            // postback hit (e.g. back-button into /checkout/processing) can't
            // reuse it, and so it doesn't linger across sessions.
            this.windowService.removeLocalStorageItem(GenericService.PAYMENT_IDENTIFIER_KEY);
        }

        const state: {
            cartError?: string,
            emailAddress?: string
        } = {};

        let redirectUrl = '/';
        const success = response?.responseSuccess;

        // Trust the cartGuid the backend returned for the verified payment
        // over the one in the frontend session. The postback arrives via an
        // external redirect, so the session's cartGuid may be stale or absent
        // (different tab, incognito, etc.).
        const cartGuid = response?.cartGuid ?? this.cartService.cartGuid;

        if (success) {
            if (cartGuid) {
                redirectUrl = `/checkout/success/${cartGuid}`;
                state.emailAddress = response.emailAddress;
            } else {
                state.cartError = 'Payment was successful, but the cart could'
                    + ' not be retrieved. Check your email for'
                    + ' the receipt.';
            }
        } else {
            state.cartError = response?.responseMessage
                ?? 'An unknown error occurred while'
                + ' processing the payment.';
        }

        this.appService.consoleLog(this, state);

        return new RedirectPaymentProcessorResponse({
            paymentIdentifier,
            cartGuid,
            status: success ? 'success' : 'cancel',
            redirectUrl,
            navigationExtras: { state }
        });
    }

    public override async payCart(params): Promise<IFramePaymentResponse> {
        const request = this._buildDto(params);

        const response = await firstValueFrom(
            this.sharedPaymentApi.makePayment(request)
        );

        return new IFramePaymentResponse({
            emailAddress: response.emailAddress,
            errorMessage: response.responseMessage,
            cartStatus: response.responseSuccess,
            processorTrackingId: response.processorTrackingId,
        });
    }

    public override async savePaymentMethod(
        params
    ): Promise<IFrameSavePaymentMethodResponse> {
        const request = new IFrameSavePaymentMethodRequestDto();
        const { paymentMethodData }: { paymentMethodData: PaymentMethodData } = params;

        this.mapRequest(
            request, paymentMethodData, params.paymentToken
        );
        request.cvvToken = params.cvvToken;
        if (params.authorizationConsent) {
            request.authorizationConsent = params.authorizationConsent;
        }

        let response: IFrameSavePaymentMethodResponseDto;

        if (params.paymentMethodId) {
            response = await firstValueFrom(
                this.sharedPaymentApi.updatePaymentMethod(
                    params.paymentMethodId, request
                )
            );
        } else {
            response = await firstValueFrom(
                this.sharedPaymentApi.addPaymentMethod(request)
            );
        }

        return new IFrameSavePaymentMethodResponse({
            paymentMethodId: response.paymentMethodId,
            last4: request.last4,
        });
    }

    public async preparePaymentAndAssessFees(
        params
    ): Promise<IFramePaymentPrepareResponse> {
        const request = this._buildDto(params);

        const response = await firstValueFrom(
            this.genericClient
                .genericPreparePaymentAndAssessFees(request)
        );

        // Cache the per-partition session tokens so the next payCart call can
        // round-trip them on its DTO. Cleared when the user changes payment
        // method or the cart shape changes (we re-prepare each time).
        this._partitionSessionTokens = (response.partitionSessionTokens ?? [])
            .map(p => new CartPartitionSessionToken({
                tellerSystemCode: p.tellerSystemCode,
                paymentProcessorConfigKey: p.paymentProcessorConfigKey,
                sessionTokenData: p.sessionTokenData
            }));

        // Refresh cart totals using the per-partition fees the plugin minted on
        // this Prepare — no second `/convenience-fee/...` round-trip.
        await this._refreshTotalsFromPrepareFees(response.partitionConvenienceFees);

        return new IFramePaymentPrepareResponse({
            paymentToken: response.paymentToken,
            convenienceFee: response.convenienceFee,
            processorPaymentMethodType:
                response.processorPaymentMethodType,
            // Phase 5: cart partitions whose plugin reports the saved method
            // needs lazy re-tokenization. Empty (or missing on unmigrated
            // integrators / ad-hoc flows) means every partition is good.
            partitionsNeedingReTokenize: (response.partitionsNeedingReTokenize ?? [])
                .map(p => new CartPartitionRef({
                    tellerSystemCode: p.tellerSystemCode,
                    paymentProcessorConfigKey: p.paymentProcessorConfigKey
                })),
            partitionSessionTokens: this._partitionSessionTokens,
            partitionConvenienceFees: response.partitionConvenienceFees ?? []
        });
    }

    /** Drive cartService.calculateTotals using fees already in hand from a Prepare
     *  response — same control flow as a regular cart-page recalc, with the
     *  callback resolving to the prepare fees instead of hitting the API. Provider
     *  is resolved lazily to keep this service free of the same DI cycle the
     *  redirect getter dodges. */
    private async _refreshTotalsFromPrepareFees(
        partitionConvenienceFees: ConvenienceFeeDto[] | undefined
    ): Promise<void> {
        const provider = this._injector.get(PaymentProcessorProvider);
        const feeConfigs = this.cartService.buildFeeConfigs(
            provider.defaultConfig,
            provider.configOverrides
        );

        await this.cartService.calculateTotals(
            feeConfigs,
            () => Promise.resolve(new ConvenienceFeeResponseDto({
                fees: partitionConvenienceFees ?? []
            }))
        );
    }

    /**
     * Lazy re-tokenization for a saved payment method against the partitions
     * the prior prepare reported as needing it (passed verbatim). The endpoint
     * re-runs Prepare server-side after tokenizing so session-bound plugins
     * (FIS-style) mint fresh partition session tokens that the subsequent
     * payCart call needs to echo on Authorize/Capture. The service caches the
     * returned tokens so {@link _buildDto} picks them up automatically — the
     * caller doesn't need to handle the response.
     */
    public async reTokenizeSavedPaymentMethod(
        cartId: number, cartGuid: string, partitions: CartPartitionRef[], cvvToken?: string
    ): Promise<void> {
        const request = new ReTokenizeSavedPaymentMethodRequestDto();
        request.cartId = cartId;
        request.cartGuid = cartGuid;
        request.cvvToken = cvvToken;
        request.partitions = (partitions ?? []).map(p => {
            const dto = new CartPartitionRefDto();
            dto.tellerSystemCode = p.tellerSystemCode;
            dto.paymentProcessorConfigKey = p.paymentProcessorConfigKey;
            return dto;
        });

        const response = await firstValueFrom(
            this.genericClient.genericReTokenizeSavedPaymentMethod(request)
        );

        // Refresh the cached per-partition session tokens from the post-tokenize
        // Prepare. payCart reads from _partitionSessionTokens via _buildDto, so
        // updating the cache is the only handoff needed.
        this._partitionSessionTokens = (response?.partitionSessionTokens ?? [])
            .map(p => new CartPartitionSessionToken({
                tellerSystemCode: p.tellerSystemCode,
                paymentProcessorConfigKey: p.paymentProcessorConfigKey,
                sessionTokenData: p.sessionTokenData
            }));

        // The post-tokenize Prepare also recomputes per-partition fees; refresh
        // totals so the user sees the up-to-date amounts before payCart fires.
        await this._refreshTotalsFromPrepareFees(response?.partitionConvenienceFees);
    }

    /**
     * Lifecycle hook called from the saved-method checkout flow after Prepare
     * and before Pay. Inspects the prepare response's
     * `partitionsNeedingReTokenize` list and minted token rows for any cart
     * partitions whose plugin doesn't yet hold one for the saved method's
     * wallet id (Phase 5 lazy re-tokenization).
     *
     * - Empty list → resolve true (nothing to do).
     * - Echeck → call re-tokenize silently (the encrypted account number on
     *   the saved method is sufficient input). Resolve true.
     * - Credit → open the {@link CvvPromptDialogComponent} modal so the user
     *   can enter their CVV via VGS, then call re-tokenize with the captured
     *   token. Resolve true on success, false if the user cancels (caller
     *   aborts pay gracefully).
     */
    public override async ensureSavedMethodReadyForPay(
        cartId: number,
        cartGuid: string,
        prepareResponse: IFramePaymentPrepareResponse,
        paymentMethodType: PaymentMethodTypeEnum
    ): Promise<boolean> {
        const partitions = prepareResponse?.partitionsNeedingReTokenize ?? [];
        if (partitions.length === 0) return true;

        if (paymentMethodType !== PaymentMethodTypeEnum.CreditCard) {
            // Echeck silent path — server has all the inputs.
            await this.reTokenizeSavedPaymentMethod(cartId, cartGuid, partitions);
            return true;
        }

        // Credit path: prompt the user for CVV via the VGS-backed modal.
        const data: CvvPromptDialogData = {
            vgsVaultId: this.vgsVaultId,
            vgsEnvironment: this.vgsEnvironment,
            vgsVersion: this.vgsVersion,
        };
        const cvvToken = await firstValueFrom(
            this.dialog.open<CvvPromptDialogComponent, CvvPromptDialogData, string | null>(
                CvvPromptDialogComponent,
                { data, disableClose: true }
            ).afterClosed()
        );

        if (!cvvToken) {
            // User cancelled the dialog — caller aborts pay so the user
            // can re-trigger when they're ready.
            return false;
        }

        await this.reTokenizeSavedPaymentMethod(cartId, cartGuid, partitions, cvvToken);
        return true;
    }

    public _buildDto(params): IFramePaymentRequestDto {
        const request = new IFramePaymentRequestDto();

        const { paymentMethodData }: { paymentMethodData: PaymentMethodData } = params;

        request.cartId = params.cartId;
        request.cartGuid = params.cartGuid;
        request.paymentMethodType = PaymentMethodTypeEnumConvertor.toDto(
                params.paymentMethodType ?? paymentMethodData.type
            );
        request.inboundRedirectSourceId = params.inboundRedirectSourceId;
        request.cvvToken = params.cvvToken;
        request.processorPaymentMethodType = params.processorPaymentMethodType;
        request.captchaToken = params.captchaToken;
        if (params.authorizationConsent) {
            request.authorizationConsent = params.authorizationConsent;
        }
        if (params.savedPaymentMethodAuthorizationConsent) {
request.savedPaymentMethodAuthorizationConsent = params.savedPaymentMethodAuthorizationConsent;
        }

        // Per-partition session tokens round-tripped from the most-recent
        // prepare so MakePayment can echo each partition's handle on its
        // Authorize / Capture / CancelAuthorization wire call. Empty when no
        // plugin initialized a session.
        request.partitionSessionTokens = this._partitionSessionTokens.map(t => {
            const dto = new CartPartitionSessionTokenDto();
            dto.tellerSystemCode = t.tellerSystemCode;
            dto.paymentProcessorConfigKey = t.paymentProcessorConfigKey;
            dto.sessionTokenData = t.sessionTokenData;
            return dto;
        });

        if (!params.saved) {
            this.mapRequest(
                request, paymentMethodData, params.paymentToken
            );
        }

        return request;
    }

    // #region helpers

    /**
     * Resolves preset tokens in redirect form input values.
     * Tokens like {paymentIdentifier} are replaced with runtime
     * values; non-token values are returned as-is.
     */
    private resolvePresetToken(
        value: string,
        startPaymentResponse: StartPaymentResponseDto,
        cartGuid: string,
        cartTotal?: number
    ): string {
        if (!value) return value;

        // Split the signed-in user's name: last word is the last name,
        // everything else is the first name. Guests have no auth details.
        const name = this.authService.authDetails?.name ?? '';
        const nameParts = name.trim().split(/\s+/).filter(p => p.length > 0);
        const lastName = nameParts.length > 1 ? (nameParts.pop() ?? '') : '';
        const firstName = nameParts.join(' ');

        // Email fallback: cart snapshot first (the user may have entered a
        // different email on checkout), then signed-in user, then inbound
        // redirect's userEmail, then empty.
        const email = this.cartService.cartSnapshot?.guestEmailAddress
            ?? this.authService.authDetails?.email
            ?? this.inboundRedirectService.userEmail
            ?? '';

        // Use global regex — the plugin emits some placeholders more than once
        // (e.g. both SignerFirstName and FirstName use {firstName}), and
        // String.prototype.replace with a string pattern only swaps the first.
        return value
            .replace(
                /\{paymentIdentifier\}/g,
                startPaymentResponse.paymentIdentifier ?? ''
            )
            .replace(/\{cartGuid\}/g, cartGuid ?? '')
            .replace(
                /\{redirectSourceId\}/g,
                this.inboundRedirectService.redirectSourceId
                    ?.toString() ?? ''
            )
            .replace(/\{email\}/g, email)
            .replace(/\{firstName\}/g, firstName)
            .replace(/\{lastName\}/g, lastName)
            .replace(/\{cartTotal\}/g, cartTotal != null ? cartTotal.toFixed(2) : '');
    }

    private addFormInput(
        form: HTMLFormElement, name: string, value: string
    ) {
        const input = this.document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.append(input);
    }

    // #endregion
}
